import React from 'react';

import { FileOutlined, InboxOutlined, SendOutlined } from '@ant-design/icons';

export const EMAIL_MATCHING_PAGE_SIZE = 10;

export const EMAIL_MATCHING_STATUS_FILTERS = {
  IMPORTED: 0,
  NEEDS_REVIEW: 1,
  MATCHED: 2,
  NO_MATCH: 3,
  AUTO_MATCHED: 4,
  SPAM: 5,
} as const;

export const EMAIL_MATCHING_STATUS_FILTER_OPTIONS = [
  { value: EMAIL_MATCHING_STATUS_FILTERS.IMPORTED, label: 'Imported' },
  { value: EMAIL_MATCHING_STATUS_FILTERS.NEEDS_REVIEW, label: 'Needs Review' },
  { value: EMAIL_MATCHING_STATUS_FILTERS.MATCHED, label: 'Matched' },
  { value: EMAIL_MATCHING_STATUS_FILTERS.NO_MATCH, label: 'No Match' },
  { value: EMAIL_MATCHING_STATUS_FILTERS.SPAM, label: 'Spam' },
];

export const EMAIL_MATCHING_STATUS_LABELS: Record<number, string> = {
  [EMAIL_MATCHING_STATUS_FILTERS.IMPORTED]: 'Imported',
  [EMAIL_MATCHING_STATUS_FILTERS.NEEDS_REVIEW]: 'Needs Review',
  [EMAIL_MATCHING_STATUS_FILTERS.MATCHED]: 'Matched',
  [EMAIL_MATCHING_STATUS_FILTERS.NO_MATCH]: 'No Match',
  [EMAIL_MATCHING_STATUS_FILTERS.AUTO_MATCHED]: 'Auto Matched',
  [EMAIL_MATCHING_STATUS_FILTERS.SPAM]: 'Spam',
};

export const EMAIL_MATCHING_STATUS_COLORS: Record<number, string> = {
  [EMAIL_MATCHING_STATUS_FILTERS.IMPORTED]: 'var(--text-secondary)',
  [EMAIL_MATCHING_STATUS_FILTERS.SPAM]: 'var(--text-secondary)',
  [EMAIL_MATCHING_STATUS_FILTERS.MATCHED]: 'var(--positive-green)',
  [EMAIL_MATCHING_STATUS_FILTERS.AUTO_MATCHED]: 'var(--mint-green)',
  [EMAIL_MATCHING_STATUS_FILTERS.NEEDS_REVIEW]: 'var(--inactive-orange)',
  [EMAIL_MATCHING_STATUS_FILTERS.NO_MATCH]: 'var(--error-red)',
};

export const EMAIL_MATCHING_SOURCES = {
  PDF_ARCHIVE: 0,
  RECEIVED_EMAIL: 1,
  SENT_EMAIL: 2,
} as const;

export const EMAIL_MATCHING_SOURCE_LABELS: Record<number, string> = {
  [EMAIL_MATCHING_SOURCES.PDF_ARCHIVE]: 'PDF Archive',
  [EMAIL_MATCHING_SOURCES.RECEIVED_EMAIL]: 'Received Email',
  [EMAIL_MATCHING_SOURCES.SENT_EMAIL]: 'Sent Email',
};

export const EMAIL_MATCHING_SOURCE_ICONS: Record<number, React.ReactNode> = {
  [EMAIL_MATCHING_SOURCES.PDF_ARCHIVE]: <FileOutlined />,
  [EMAIL_MATCHING_SOURCES.RECEIVED_EMAIL]: <InboxOutlined />,
  [EMAIL_MATCHING_SOURCES.SENT_EMAIL]: <SendOutlined />,
};

export const EMAIL_MATCHING_IDENTITY_SIGNAL_MISMATCH_NOTES: Record<string, string> = {
  community_name: 'No match — name differs from this community',
  address: 'No match — address is not on file for this community',
  phone_number: 'No match — number is not on file for this community',
  company_name: "No match — company differs from this community's company",
  email_domain: "No match — domain is not on file for this community's company",
};

export const EMAIL_MATCHING_SOURCE_FILTER_OPTIONS = [
  {
    value: EMAIL_MATCHING_SOURCES.PDF_ARCHIVE,
    label: EMAIL_MATCHING_SOURCE_LABELS[EMAIL_MATCHING_SOURCES.PDF_ARCHIVE],
  },
  {
    value: EMAIL_MATCHING_SOURCES.RECEIVED_EMAIL,
    label: EMAIL_MATCHING_SOURCE_LABELS[EMAIL_MATCHING_SOURCES.RECEIVED_EMAIL],
  },
  { value: EMAIL_MATCHING_SOURCES.SENT_EMAIL, label: EMAIL_MATCHING_SOURCE_LABELS[EMAIL_MATCHING_SOURCES.SENT_EMAIL] },
];

export const EMAIL_MATCHING_STATUS_TABS = {
  ALL: 'all',
  MATCHED: 'matched',
  NEEDS_REVIEW: 'needs_review',
  NO_MATCH: 'no_match',
  SPAM: 'spam',
} as const;

export type EmailMatchingStatusTabType = (typeof EMAIL_MATCHING_STATUS_TABS)[keyof typeof EMAIL_MATCHING_STATUS_TABS];

// Must stay in sync with EmailMatchingCommunicationType in email_matching/enums.py (backend).
export const EMAIL_MATCHING_COMMUNICATION_TYPES = {
  WELCOME: 1,
  INQUIRY_RESPONSE: 2,
  SALES_FOLLOW_UP: 3,
  LEAD_RE_ENGAGEMENT: 4,
  TOURS: 5,
  EVENTS: 6,
  INCENTIVES: 7,
  PRICING: 8,
  AVAILABILITY: 9,
  NEWS: 10,
  BRAND: 11,
  REVIEWS: 12,
  CARE: 13,
  DINING: 14,
  AMENITIES: 15,
  SOCIAL: 16,
  SEASONAL: 17,
  OTHER: 18,
} as const;

export const EMAIL_MATCHING_COMMUNICATION_TYPE_OPTIONS = [
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.WELCOME, label: 'Welcome' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.INQUIRY_RESPONSE, label: 'Inquiry Response' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.SALES_FOLLOW_UP, label: 'Sales Follow-Up' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.LEAD_RE_ENGAGEMENT, label: 'Lead Re-Engagement' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.TOURS, label: 'Tours' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.EVENTS, label: 'Events' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.INCENTIVES, label: 'Incentives' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.PRICING, label: 'Pricing' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.AVAILABILITY, label: 'Availability' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.NEWS, label: 'News' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.BRAND, label: 'Brand' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.REVIEWS, label: 'Reviews' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.CARE, label: 'Care' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.DINING, label: 'Dining' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.AMENITIES, label: 'Amenities' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.SOCIAL, label: 'Social' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.SEASONAL, label: 'Seasonal' },
  { value: EMAIL_MATCHING_COMMUNICATION_TYPES.OTHER, label: 'Other' },
];

export const EMAIL_MATCHING_COMMUNICATION_TYPE_LABELS: Record<number, string> =
  EMAIL_MATCHING_COMMUNICATION_TYPE_OPTIONS.reduce(
    (labels, option) => ({ ...labels, [option.value]: option.label }),
    {} as Record<number, string>
  );

export const EMAIL_MATCHING_DELIVERY_METHODS = {
  DIRECT_EMAIL: 1,
  MARKETING_PLATFORM: 2,
} as const;

export const EMAIL_MATCHING_DELIVERY_METHOD_OPTIONS = [
  { value: EMAIL_MATCHING_DELIVERY_METHODS.DIRECT_EMAIL, label: 'Direct Email' },
  { value: EMAIL_MATCHING_DELIVERY_METHODS.MARKETING_PLATFORM, label: 'Marketing Platform' },
];

export const EMAIL_MATCHING_DELIVERY_METHOD_LABELS: Record<number, string> = {
  [EMAIL_MATCHING_DELIVERY_METHODS.DIRECT_EMAIL]: 'Direct Email',
  [EMAIL_MATCHING_DELIVERY_METHODS.MARKETING_PLATFORM]: 'Marketing Platform',
};

export const EMAIL_MATCHING_SENDER_TYPES = {
  COMMUNITY_REPRESENTATIVE: 1,
  MARKETING_AUTOMATION: 2,
} as const;

export const EMAIL_MATCHING_SENDER_TYPE_OPTIONS = [
  { value: EMAIL_MATCHING_SENDER_TYPES.COMMUNITY_REPRESENTATIVE, label: 'Community Representative' },
  { value: EMAIL_MATCHING_SENDER_TYPES.MARKETING_AUTOMATION, label: 'Marketing Automation' },
];

export const EMAIL_MATCHING_SENDER_TYPE_LABELS: Record<number, string> = {
  [EMAIL_MATCHING_SENDER_TYPES.COMMUNITY_REPRESENTATIVE]: 'Community Representative',
  [EMAIL_MATCHING_SENDER_TYPES.MARKETING_AUTOMATION]: 'Marketing Automation',
};
